import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import * as isAfter from 'date-fns/is_after';
import * as subWeeks from 'date-fns/sub_weeks';
import * as fileSaver from 'file-saver';
import {BehaviorSubject, Subscription} from 'rxjs';
import {AnalyticsService} from '../../shared/analytics.service';
import {HttpError} from '../../shared/api.service';
import {AppStateService} from '../../shared/app-state.service';
import {AuthService} from '../../shared/auth.service';
import {StorageService} from '../../shared/storage.service';
import {ClientApiService} from '../client-api.service';

const EMAIL_COPY_STORAGE_KEY = 'reports.emailCopy';

function formatDate(date: Date) {
  return date.toISOString().slice(0, 10);
}

@Component({
  selector: 'reports-page',
  templateUrl: './reports-page.component.html',
  styleUrls: ['./reports-page.component.scss']
})
export class ReportsPageComponent implements OnInit, OnDestroy {

  typeOptions = new Map<string, [string, {}]>([
    ['billing', ['reports.type.billing', {}]],
    ['hours', ['reports.type.hours', {}]],
    ['staffing', ['reports.type.staffing', {}]]
  ]);

  readonly SMALL_SCREEN_BREAKPOINT = 640;

  reportForm: FormGroup;
  storageSub: Subscription;
  loading = false;
  successMessage = false;
  noData$ = new BehaviorSubject(false);
  innerWidth: number;

  brand$ = this.AppState.brand$;
  masquerading$ = this.Auth.masquerading$;

  constructor(private Form: FormBuilder,
              private Storage: StorageService,
              private Api: ClientApiService,
              private Analytics: AnalyticsService,
              private Auth: AuthService,
              private AppState: AppStateService) {
  }

  ngOnInit(): void {
    let shouldEmailCopy: boolean = this.Storage.get(EMAIL_COPY_STORAGE_KEY, true);
    if (this.masquerading$.getValue()) {
      shouldEmailCopy = false;
    }
    this.onResize(null);

    this.reportForm = this.Form.group({
      reportType: ['billing', Validators.required],
      emailCopy: [shouldEmailCopy],
      fromDate: [formatDate(subWeeks(new Date(), 1)), Validators.required],
      toDate: [formatDate(new Date()), Validators.required]
    }, {
      validator: (): ValidationErrors | null => {
        return !this.reportForm || isAfter(this.fromDate.value, this.toDate.value) ? {dateRange: true} : null;
      }
    });
    this.storageSub = this.emailCopy.valueChanges
      .subscribe(value => this.Storage.put(EMAIL_COPY_STORAGE_KEY, value));
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Object): void {
    this.innerWidth = window.innerWidth;
  }

  trackReports(params): void {
    this.Analytics.trackEvent('Run Report');
    if (params.emailCopy) {
      this.Analytics.trackEvent('Email Report');
    }
    this.Analytics.trackEvent(
      params.reportType.charAt(0).toUpperCase() + params.reportType.slice(1) + ' Report',
      {
        emailCopy: params.emailCopy,
        fromDate: params.fromDate,
        toDate: params.toDate
      });
  }

  ngOnDestroy(): void {
    if (this.storageSub) this.storageSub.unsubscribe();
  }

  get emailCopy() {
    return this.reportForm && this.reportForm.get('emailCopy');
  }

  get fromDate() {
    return this.reportForm && this.reportForm.get('fromDate');
  }

  get toDate() {
    return this.reportForm && this.reportForm.get('toDate');
  }

  toggleValue(control: string): void {
    if (this.masquerading$.getValue()) {
      return;
    }
    this.reportForm.patchValue({
      [control]: !this.reportForm.get(control).value
    });
  }

  resetData(): void {
    this.noData$.next(false);
  }

  submit(): void {
    this.loading = true;

    const params = this.reportForm.getRawValue();
    this.Api.getReport(params)
      .subscribe(data => {
        if (!data) {
          this.loading = false;
          this.noData$.next(true);
        } else {
          if (this.innerWidth > this.SMALL_SCREEN_BREAKPOINT) {
            fileSaver.saveAs(data);
          } else {
            this.successMessage = true;
          }
          this.loading = false;
        }
      }, (err: HttpError) => {
        console.error('Failed to save report.', err);
        this.loading = false;
      });
    this.trackReports(params);
  }
}
